import { SelectInput } from '@abyss/web/ui/SelectInput';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetMultiCounty } from '../../hooks/useGetMultiCounty/useGetMultiCounty';

export type CountyStateOption = {
  label: string;
  value: string;
  stateCode?: string;
  countyId?: string;
};

export type CountyStateSelectInputProps = {
  usPostalPointId: string;
  show: boolean;
  isCountyList: boolean;
  selectedValue: string;
  onChange: (
    value: string | number | Function,
    selectedOption?: Record<string, any>
  ) => void;
  label?: string;
  placeholder?: string;
  width?: number | string;
  disabled?: boolean;
  'data-testid'?: string;
  'data-auto-testid'?: string;
};

export const CountyStateSelectInput = ({
  usPostalPointId,
  show,
  isCountyList,
  selectedValue,
  onChange,
  label,
  placeholder,
  width = 300,
  disabled = false,
  'data-testid': dataTestId,
  'data-auto-testid': dataAutoTestId,
}: CountyStateSelectInputProps) => {
  const { t } = useTranslation();
  const [multiCountyResult, getMultiCounty] = useGetMultiCounty({});

  // Fetch multi-county data when usPostalPointId changes
  useEffect(() => {
    if (usPostalPointId && show) {
      getMultiCounty({
        variables: {
          usPostalPointId: String(usPostalPointId),
        },
      });
    }
  }, [usPostalPointId, show]);

  // Transform multi-county data into select options using useMemo
  const options = useMemo(() => {
    if (!multiCountyResult?.data?.getMultiCounty) {
      return [];
    }

    const multiCountyData = multiCountyResult.data.getMultiCounty;
    const multiCountyItems = Array.isArray(multiCountyData)
      ? multiCountyData
      : [multiCountyData];

    const relatedRecords = multiCountyItems
      .flatMap((item) => item?.relatedRecords || [])
      .filter(Boolean);

    return relatedRecords
      .map((record) => {
        const attributes = record?.attributes || {};
        const countyLabel =
          attributes.county_proper || attributes.county || attributes.state;
        const stateCode = attributes.state || attributes.state_id;
        const countyId = attributes.county_id || attributes.ID || '';

        if (!countyLabel || !stateCode) {
          return null;
        }

        return {
          label: `${countyLabel}, ${stateCode}`,
          value: `${stateCode}-${countyId || countyLabel}`,
          stateCode,
          countyId,
        } as CountyStateOption;
      })
      .filter((option): option is CountyStateOption => option !== null);
  }, [multiCountyResult]);

  const isLoading = multiCountyResult?.isLoading || false;

  if (!show) {
    return null;
  }

  const defaultLabel = isCountyList
    ? t('GUEST_COUNTY_STATE_MODAL.COUNTY')
    : t('GUEST_COUNTY_STATE_MODAL.STATE');

  const defaultPlaceholder = t('GUEST_COUNTY_STATE_MODAL.SELECT');

  return (
    <SelectInput
      data-auto-testid={dataAutoTestId || 'county-state-select-input'}
      data-testid={dataTestId || 'county-state-select-input'}
      isDisabled={disabled || isLoading}
      label={label || defaultLabel}
      onChange={onChange}
      options={options}
      placeholder={placeholder || defaultPlaceholder}
      value={selectedValue}
      width={width}
    />
  );
};
